import styled from 'styled-components';
import { fmtAdvisorMonth, fmtAdvisorReset, type AdvisorUsage } from '@entities/progress';

/* Счётчик лимита Эксперта: сколько процентов месячного лимита осталось, полоса
   и дата обнуления. Денег не показываем — ценник отпугивал от Эксперта. */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 200px;
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const Strong = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const Track = styled.div`
  height: 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surfaceHover};
  overflow: hidden;
`;

const Fill = styled.div<{ $share: number; $tone: 'ok' | 'warn' | 'over' }>`
  height: 100%;
  width: ${({ $share }) => `${Math.round($share * 100)}%`};
  border-radius: inherit;
  background: ${({ $tone, theme }) =>
    $tone === 'over'
      ? theme.colors.status.danger
      : $tone === 'warn'
        ? theme.colors.status.warning
        : theme.colors.status.success};
  transition: width ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

export function UsageMeter({ usage }: { usage: AdvisorUsage | undefined }) {
  if (!usage) {
    return (
      <Wrap aria-busy>
        <Line>Лимит месяца…</Line>
        <Track />
      </Wrap>
    );
  }
  // Полоса показывает остаток — ровно то, что написано рядом словами; на
  // исчерпанном лимите заливаем её целиком красным, иначе сигнала не видно.
  const share = usage.exhausted ? 1 : Math.min(1, Math.max(0, usage.remaining_percent / 100));
  const tone = usage.exhausted ? 'over' : usage.remaining_percent <= 20 ? 'warn' : 'ok';
  const reset = fmtAdvisorReset(usage.resets_on);
  return (
    <Wrap
      title={`Лимит вопросов на ${fmtAdvisorMonth(usage.month)}. Модель: ${usage.model}. Обнулится ${reset}.`}
    >
      <Line>
        <span>
          {usage.exhausted ? (
            'Лимит исчерпан'
          ) : (
            <>
              Осталось <Strong>{usage.remaining_percent}%</Strong> лимита
            </>
          )}
        </span>
        <span>обновится {reset}</span>
      </Line>
      <Track
        role="meter"
        aria-label="Остаток лимита за месяц, %"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={usage.remaining_percent}
        aria-valuetext={`осталось ${usage.remaining_percent}%`}
      >
        <Fill $share={share} $tone={tone} />
      </Track>
    </Wrap>
  );
}
